import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';

import { dataTableSx, HeadLabelCell, stickyTotalCellSx } from '../../../shared/components/TableHeader';
import {
  EMPTY_VALUE, formatEf, formatKwh, formatTonne,
} from '../../../shared/utils/number';
import { SCOPE_TOKENS, TOKENS } from '../../../theme/muiTheme';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import useScope2Emissions from '../hooks/useScope2Emissions';
import EmissionPanel from './EmissionPanel';
import EmissionOverviewNotice from './EmissionOverviewNotice';
import OfficeEmissionsTreemap from './OfficeEmissionsTreemap';
import ScopeHero from './ScopeHero';
import ScopeTrendChart from './ScopeTrendChart';

const ROWS_PER_PAGE = 15;

export default function Scope2Page() {
  const { t } = useTranslation();
  const {
    year, setYear, years, data, loading, error, reload,
  } = useScope2Emissions();
  const scope = data.scope2;
  const isHistorical = Boolean(data.meta.isHistorical);
  const [page, setPage] = useState(0);
  const visibleOffices = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return scope.offices.slice(start, start + ROWS_PER_PAGE);
  }, [page, scope.offices]);
  useEffect(() => setPage(0), [year, scope.offices]);
  const hasData = data.meta.status.scope2 !== 'EMPTY';
  const anomalies = data.meta.anomalies.filter((item) => item.startsWith('SCOPE2'));
  usePagePresentation({
    loading,
    status: error ? null : data.meta.status.scope2,
    estimation: error ? null : data.meta.estimatedFactors.scope2,
  });
  const canRenderData = !error;

  return (
    <Box component="section" aria-label={t('scopeEmissions.scope2.title')} className="page-stack">
      <ScopeHero
        title={t('scopeEmissions.scope2.title')}
        total={scope.total}
        year={year}
        years={years}
        onYearChange={setYear}
        tone={SCOPE_TOKENS.SCOPE_2}
        icon={BoltOutlinedIcon}
        yearLabel={t('scopeEmissions.common.reportingYear')}
      />

      <EmissionOverviewNotice
        error={error}
        hasData={hasData}
        anomalies={anomalies}
        onRetry={reload}
      />

      {canRenderData && (
        <>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 7fr) minmax(360px, 5fr)',
            ...(isHistorical ? { lg: 'minmax(0, 1fr)' } : {}),
          },
        }}
      >
        {!isHistorical && <EmissionPanel
          title={t('scopeEmissions.scope2.chartTitle')}
          icon={StackedLineChartOutlinedIcon}
          unit="tCO₂e"
          sx={{
            height: '100%',
            '& .MuiCardContent-root': {
              display: { lg: 'flex' },
              flexDirection: { lg: 'column' },
              height: { lg: '100%' },
            },
          }}
        >
          <ScopeTrendChart
            data={scope.monthly}
            color={SCOPE_TOKENS.SCOPE_2.color}
            unit="tCO₂e"
            dataKey="emissions"
            chartId="scope2-monthly-gradient"
            ariaLabel={t('scopeEmissions.scope2.chartAria', { year })}
            fillAvailableHeight
          />
        </EmissionPanel>}

        <EmissionPanel
          title={t('scopeEmissions.scope2.treemapTitle')}
          icon={GridViewOutlinedIcon}
          unit="tCO₂e"
          sx={{ height: '100%' }}
        >
          <OfficeEmissionsTreemap
            offices={scope.offices}
            showActivity={!isHistorical}
          />
        </EmissionPanel>
      </Box>

      <EmissionPanel
        title={t('scopeEmissions.scope2.tableTitle')}
        icon={DomainOutlinedIcon}
      >
        <TableContainer sx={{ maxHeight: 460, overflow: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={(theme) => ({ ...dataTableSx(theme), minWidth: 900 })}
          >
            <TableHead>
              <TableRow>
                <HeadLabelCell width={52}>{t('scopeEmissions.scope2.column.rank')}</HeadLabelCell>
                <HeadLabelCell>{t('scopeEmissions.scope2.column.office')}</HeadLabelCell>
                {!isHistorical && <HeadLabelCell>{t('scopeEmissions.scope2.column.legalEntity')}</HeadLabelCell>}
                {!isHistorical && <HeadLabelCell align="right">{t('scopeEmissions.scope2.column.electricity')}</HeadLabelCell>}
                {!isHistorical && <HeadLabelCell align="right">{t('scopeEmissions.scope2.column.factor')}</HeadLabelCell>}
                <HeadLabelCell align="right">{t('scopeEmissions.scope2.column.emissions')}</HeadLabelCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleOffices.length === 0 && (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={6}
                    sx={{ color: 'text.secondary', py: 3 }}
                  >
                    {EMPTY_VALUE}
                  </TableCell>
                </TableRow>
              )}
              {visibleOffices.map((office, index) => (
                <TableRow key={office.id} hover>
                  <TableCell color="text.secondary">{page * ROWS_PER_PAGE + index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {office.name || EMPTY_VALUE}
</Typography>
                    {office.registrationNo && (
                      <Typography variant="caption" color="text.secondary">
                        {office.registrationNo}
                      </Typography>
                    )}
                  </TableCell>
                    {!isHistorical && <TableCell>{office.legalEntityCode || office.companyNameEn || EMPTY_VALUE}</TableCell>}
                    {!isHistorical && <TableCell align="right">{formatKwh(office.kwh)}</TableCell>}
                    {!isHistorical && <TableCell align="right">
                    {office.factorStatus === 'INCONSISTENT' ? (
                      <Tooltip title={t('emissionOverview.inconsistentFactor')}>
                        <span>{EMPTY_VALUE}</span>
                      </Tooltip>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column' }}>
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {formatEf(office.emissionFactor)}
                          {office.renewable && (
                            <Tooltip title={t('scopeEmissions.scope2.greenElectricity')}>
                              <EnergySavingsLeafOutlinedIcon
                                aria-label={t('scopeEmissions.scope2.greenElectricity')}
                                sx={{ color: TOKENS.success, fontSize: 17 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {office.sourceYear && (
                          <Typography
                            variant="caption"
                            color={office.isEstimated ? 'warning.main' : 'text.secondary'}
                          >
                            {t(office.isEstimated
                              ? 'scopeEmissions.scope2.carryForwardSourceYear'
                              : 'scopeEmissions.scope2.factorSourceYear', {
                              year: office.sourceYear,
                            })}
                          </Typography>
                        )}
                      </Box>
                    )}
                    </TableCell>}
                  <TableCell
                    align="right"
                    sx={{
                      color: office.emissions === 0 ? TOKENS.success : SCOPE_TOKENS.SCOPE_2.dark,
                      fontWeight: 750,
                    }}
                  >
                    {formatTonne(office.emissions)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={stickyTotalCellSx} />
                <TableCell sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{t('scopeEmissions.common.total')}</TableCell>
                {!isHistorical && <TableCell sx={stickyTotalCellSx} />}
                {!isHistorical && <TableCell align="right" sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{formatKwh(scope.totalKwh)}</TableCell>}
                {!isHistorical && <TableCell sx={stickyTotalCellSx} />}
                <TableCell align="right" sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{formatTonne(scope.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={scope.offices.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[]}
        />
      </EmissionPanel>
        </>
      )}
    </Box>
  );

}
